import { useEffect, useState } from "react";
import "../../css/StorePayoutRequests.css";
import {
    fetchStorePayoutRequests,
    sendStorePayoutRequest,
    sendAllStorePayoutRequests,
} from "../../service/api/adminApi";

const BACKEND_URL = "https://food.totalfresh.co";
const RESTAURANT_IMAGE_URL = "https://food.totalfresh.co";

const getImageSrc = (row) => {
    const image = [
        row?.image_url,
        row?.restaurant_image_url,
        row?.store_image_url,
        row?.logo_url,
        row?.image,
        row?.restaurant_image,
        row?.store_image,
        row?.logo,
        row?.restaurant?.image_url,
        row?.restaurant?.image,
    ]
        .map((value) => (value === null || value === undefined ? "" : String(value).trim()))
        .find((value) => value && value !== "null" && value !== "undefined");

    if (!image) return null;

    const normalizedImage = image
        .replace("https://localhost:8000", "http://localhost:8000")
        .replace("https://127.0.0.1:8000", "http://127.0.0.1:8000");

    if (
        normalizedImage.startsWith("http://") ||
        normalizedImage.startsWith("https://") ||
        normalizedImage.startsWith("//") ||
        normalizedImage.startsWith("data:") ||
        normalizedImage.startsWith("blob:")
    ) {
        return normalizedImage;
    }

    if (normalizedImage.startsWith("storage/") || normalizedImage.startsWith("uploads/") || normalizedImage.startsWith("assets/")) {
        return `${BACKEND_URL}/${normalizedImage}`;
    }

    return normalizedImage.startsWith("/")
        ? `${RESTAURANT_IMAGE_URL}${normalizedImage}`
        : `${RESTAURANT_IMAGE_URL}/${normalizedImage}`;
};

const OwnerArrow = () => <span className="spr-owner-arrow">›</span>;

export default function StorePayoutRequests() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [requestingId, setRequestingId] = useState(null);
    const [sendingAll, setSendingAll] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [failedImages, setFailedImages] = useState({});
    const [refreshKey, setRefreshKey] = useState(0);

    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0,
    });

    useEffect(() => {
        const controller = new AbortController();

        const loadRequests = async () => {
            try {
                setLoading(true);

                const res = await fetchStorePayoutRequests(
                    {
                        search,
                        per_page: perPage,
                        page,
                    },
                    controller.signal
                );

                if (controller.signal.aborted) return;

                const pagination = res?.meta || {};

                setRows(res?.data || []);
                setMeta({
                    current_page: pagination.current_page || page,
                    last_page: pagination.last_page || 1,
                    total: pagination.total || 0,
                    from: pagination.from || 0,
                    to: pagination.to || 0,
                });
            } catch (error) {
                if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
                    return;
                }

                console.error(error);
                alert(error?.response?.data?.message || "Failed to load payout requests");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadRequests();

        return () => controller.abort();
    }, [search, perPage, page, refreshKey]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSearch(searchInput);
        }, 600);

        return () => clearTimeout(timer);
    }, [searchInput]);
    const resetFilters = () => {
        setSearchInput("");
        setSearch("");
        setPerPage(10);
        setPage(1);
    };

    const handleSendRequest = async (row) => {
        if (!window.confirm(`Send payout request for ${row.restaurant_name}?`)) return;

        try {
            setRequestingId(row.restaurant_id);
            await sendStorePayoutRequest({ restaurant_id: row.restaurant_id });
            alert("Payout request sent.");
            setRefreshKey((value) => value + 1);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to send payout request");
        } finally {
            setRequestingId(null);
        }
    };

    const handleSendAll = async () => {
        if (!window.confirm("Are you sure you want to send payout requests for all stores?")) return;

        try {
            setSendingAll(true);
            const res = await sendAllStorePayoutRequests();
            alert(res?.message || res?.success || "Payout requests sent.");
            setRefreshKey((value) => value + 1);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to send all payout requests");
        } finally {
            setSendingAll(false);
        }
    };

    const loginAsOwner = (ownerId) => {
        if (!ownerId) return;
        window.location.href = `${BACKEND_URL}/admin/impersonate/${ownerId}`;
    };


    const exportCsv = () => {
        const header = ["Image", "Name", "Owner", "Amount", "Gross", "Commission", "Joined Date"];

        const body = rows.map((row) => [
            getImageSrc(row) || "",
            row.restaurant_name || "",
            row.owners?.length ? row.owners.map((o) => o.name).join(" | ") : "Unassigned",
            Number(row.payable_amount || 0).toFixed(2),
            Number(row.gross_amount || 0).toFixed(2),
            `${Number(row.commission_rate || 0).toFixed(2)}%`,
            row.created_at || "",
        ]);

        const csv = [header, ...body].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `stores-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();

        URL.revokeObjectURL(url);
    };

    return (
        <div className="spr-page">
            <div className="spr-head">
                <div>
                    <h2>Stores Management</h2>
                    <p>Send payout requests for eligible stores</p>
                </div>

                <div className="spr-actions">
                    <button type="button" className="spr-reset-btn" onClick={resetFilters}>
                        Reset All Filters
                    </button>

                    <button
                        type="button"
                        className="spr-send-all-btn"
                        disabled={sendingAll || loading || rows.length === 0}
                        onClick={handleSendAll}
                    >
                        {sendingAll ? "Sending..." : "Send All Payout Requests"}
                    </button>
                </div>
            </div>

            <div className="spr-card">
                <div className="spr-toolbar">
                    <input
                        className="spr-search"
                        type="text"
                        placeholder="Search with anything..."
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                        }}
                    />

                    <div className="spr-actions">
                        <button type="button" className="spr-export-btn" onClick={exportCsv}>
                            Export as CSV
                        </button>

                        <select
                            className="spr-per-page"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            {[10, 25, 50, 100, 200, 500].map((value) => (
                                <option key={value} value={value}>
                                    {value}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="spr-table-wrap">
                    <table className="spr-table">
                        <thead>
                        <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Owner</th>
                            <th>Balance</th>
                            <th>Request</th>
                            <th>Joined Date</th>
                        </tr>
                        </thead>

                        <tbody>
                        {rows.map((row) => {
                            const imageSrc = getImageSrc(row);

                            return (
                                <tr key={row.restaurant_id}>
                                    <td>
                                        <div className="spr-image-box">
                                            {imageSrc && failedImages[row.restaurant_id] !== imageSrc ? (
                                                <img
                                                    src={imageSrc}
                                                    alt={row.restaurant_name || "Store"}
                                                    className="spr-store-img"
                                                    onError={() => {
                                                        setFailedImages((prev) => ({
                                                            ...prev,
                                                            [row.restaurant_id]: imageSrc,
                                                        }));
                                                    }}
                                                />
                                            ) : (
                                                <div className="spr-image-placeholder">No Image</div>
                                            )}

                                            {/* <StoreSwitch checked={!!row.is_promoted} /> */}
                                        </div>
                                    </td>

                                    <td>
                                        <strong>{row.restaurant_name || "--"}</strong>
                                        <div className="spr-zone-name">{row.zone_name || ""}</div>
                                    </td>

                                    <td>
                                        {row.owners?.length ? (
                                            row.owners.map((owner) => (
                                                <button
                                                    key={owner.id}
                                                    type="button"
                                                    className="spr-owner"
                                                    onClick={() => loginAsOwner(owner.id)}
                                                    title="Login as owner"
                                                >
                                                    <span>{owner.name}</span>
                                                    <OwnerArrow />
                                                </button>
                                            ))
                                        ) : (
                                            <span>Unassigned</span>
                                        )}
                                    </td>

                                    <td>
                                        <div className="spr-amount-box">
                      <span className="spr-amount">
                        ₹{Number(row.payable_amount || 0).toFixed(2)}
                      </span>
                                            <span>Gross ₹{Number(row.gross_amount || 0).toFixed(2)}</span>
                                            <span>Commission {Number(row.commission_rate || 0).toFixed(2)}%</span>
                                        </div>
                                    </td>

                                    <td>
                                        <button
                                            type="button"
                                            className="spr-request-btn"
                                            disabled={requestingId === row.restaurant_id}
                                            onClick={() => handleSendRequest(row)}
                                        >
                                            {requestingId === row.restaurant_id ? "Sending..." : "Send Request"}
                                        </button>
                                    </td>

                                    <td>{row.created_at || "--"}</td>


                                </tr>
                            );
                        })}

                        {loading && (
                            <tr>
                                <td colSpan="6" className="spr-empty">
                                    Loading...
                                </td>
                            </tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan="6" className="spr-empty">
                                    No eligible stores found
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="spr-pagination">
          <span>
            {meta.total > 0
                ? `Showing ${meta.from || (page - 1) * perPage + 1} to ${
                    meta.to || (page - 1) * perPage + rows.length
                } of ${meta.total} entries`
                : "Showing 0 entries"}
          </span>

                    <div className="spr-page-buttons">
                        <button
                            type="button"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        >
                            Previous
                        </button>

                        <span>
              Page {meta.current_page || page} of {meta.last_page}
            </span>

                        <button
                            type="button"
                            disabled={loading || page >= meta.last_page}
                            onClick={() => setPage((p) => Math.min(p + 1, meta.last_page))}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}